import { PaymentMethodApi } from "@app/services/apiServices/PaymentMethodApi";
import { PaymentMethod } from "@app/models/PaymentMethod";
import { FTNIApi, FTNIAchTokenResponse } from "../../../services/apiServices/FTNIApi";
import { UtilService } from "@app/services/Util.service";
import { CheckingAccount } from "@app/models/CheckingAccount";
import { SpinnerService } from "@app/services/spinner.service";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { Property } from "@app/models/Property";
import { UserStore } from "@app/services/stores/UserStore";
import { CouponCaptureService } from "@app/services/coupon-capture.service";
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { PaymentScheduleApi } from "@app/services/apiServices/PaymentScheduleApi";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { DetailsPropagateDefaultDialog } from "@app/pages/wallet/dialogs/details-propagate-default.dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { AccountDeclinedDialog } from "@app/pages/wallet/checkadd/accountDeclined.dialog";
import { CheckRoutingNumberHelperDialog } from "@app/pages/wallet/checkadd/checkRoutingNumberHelper.dialog";
import { CheckAccountNumberHelperDialog } from "@app/pages/wallet/checkadd/checkAccountNumberHelper.dialog";
import { Router } from "@angular/router";

/**
 *  CheckingAccountAddComponent add checking account payment method.
 */
@Component({
    selector: 'app-checking-account-add',
    template: require('./checkingAccountAdd.html')
})
export class CheckingAccountAddComponent implements OnInit{

    @ViewChild('checkingAccountAdd') private checkingAccountAddForm: NgForm;

    checkingAccount: CheckingAccount;
    paymentMethod: PaymentMethod;
    tokenResponse: FTNIAchTokenResponse;
    isValidRoutingNumber: boolean;
    routingErrorMsg: string;
    validAccount: boolean;
    accountNumberErrorMsg: string;
    accountNumberConfirmErrorMsg: string;
    comingFromPayNow: boolean;
    propertyId: any;
    property: Property;
    $stateParams: any;

    private paymentSchedules: PaymentSchedule[];

    constructor(@Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
                @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(FTNIApi) private ftniApi: FTNIApi,
                @Inject(UtilService) private utilService: UtilService,
                @Inject(MatDialog) private dialog: MatDialog,
                @Inject(CouponCaptureService) private couponCaptureService: CouponCaptureService,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(Router) private router: Router
    ) {

        if(this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state;
        }

    }

    ngOnInit() {

        this.checkingAccount = {
            routingNumber: "",
            accountNumber: "",
            accountNumberConfirm: "",
            nickname: "",
            defaultPaymentMethod: false,
            accountHolderName: "",
            bankName: "",
            payerId: null,
            savingsAccountIndicator: "checking"
        };

        if (this.$stateParams) {
            this.comingFromPayNow = this.$stateParams.comingFromPayNow ? this.$stateParams.comingFromPayNow : false;
            this.propertyId = this.$stateParams.propertyId ? this.$stateParams.propertyId : null;
            this.property = this.$stateParams.property ? this.$stateParams.property : null;
        }

        this.checkingAccount.payerId = this.userStore.getHoaHomeownerId();
        this.checkingAccount.accountHolderName = this.userStore.getFirstName() + ' ' + this.userStore.getLastName();
        this.checkingAccount.routingNumber = this.couponCaptureService.getRoutingNumber();
        this.checkingAccount.accountNumber = this.couponCaptureService.getAccountNumber();
        this.checkingAccount.accountNumberConfirm = this.couponCaptureService.getAccountNumber();

        // call checkRoutingNumber() right away if we have a routingNumber
        if (this.checkingAccount.routingNumber) {
            this.checkRoutingNumber();
        }
        this.paymentMethodApi.getDefaultPaymentMethodSchedules(this.userStore.getHoaHomeownerId())
            .pipe(take(1)).subscribe({
                next: resp => {
                    this.paymentSchedules = resp;
                },
                error: () => {
                }
            });
    }

    /**
     * Calls paymentMethodsRoutingNumberGet to verify that routing number entered is valid.
     */
    checkRoutingNumber() {
        if (this.checkingAccount.routingNumber && this.checkingAccount.routingNumber.length === 9) {
            //call api that takes routing number and returns bank name
            this.paymentMethodApi.paymentMethodsRoutingNumberGet(this.checkingAccount.routingNumber, false)
                .pipe(take(1)).subscribe({
                    next: resp => {
                        this.isValidRoutingNumber = resp.isValidRoutingNumber;
                        if (this.isValidRoutingNumber) {
                            this.checkingAccount.bankName = resp.bankName;
                            this.checkingAccount.nickname = this.utilService.toTitleCase(this.checkingAccount.bankName);
                            this.routingErrorMsg = null;

                            this.numberValidation();
                        } else {
                            this.checkingAccount.bankName = null;
                            this.routingErrorMsg = "Please enter a valid routing number.";
                        }
                    }, error: err => {
                        this.checkingAccount.bankName = null;
                        this.isValidRoutingNumber = false;
                        this.routingErrorMsg = "Please enter a valid routing number.";
                        this.networkErrorService.handleNetworkError(err, true)
                            .pipe(take(1)).subscribe((resp) => {
                                if (resp === true) {
                                    this.checkRoutingNumber();
                                }
                            });
                    }
                });

        } else {
            this.checkingAccount.bankName = null;
            this.isValidRoutingNumber = false;
            this.routingErrorMsg = "Please enter a valid routing number.";
        }
    }

    //NOT TESTING due to complexity of testing angular forms with current setup
    /**
     * Checks that account numbers entered match.
     */
    numberValidation() {
        if (this.checkingAccountAddForm.form.controls.accountNumber.errors === null ) {
            this.accountNumberErrorMsg = null;
            this.validAccount = false;
        }
        if (this.checkingAccountAddForm.form.controls.accountNumberConfirm.errors === null) {
            this.accountNumberConfirmErrorMsg = null;
            this.validAccount = false;
        }

        if (this.checkingAccountAddForm.form.controls.accountNumber.errors ||
            this.checkingAccountAddForm.form.controls.accountNumberConfirm.errors) {
            if (this.checkingAccount.accountNumber &&
                (this.checkingAccount.accountNumber.length < 3 || this.checkingAccount.accountNumber.length > 17)) {
                this.accountNumberErrorMsg = "Please enter a valid account number."
            }
            if (this.checkingAccount.accountNumberConfirm &&
                (this.checkingAccount.accountNumberConfirm.length < 3 || this.checkingAccount.accountNumberConfirm.length > 17)) {
                this.accountNumberConfirmErrorMsg = "Please enter a valid account number."
            }
            return;
        }

        if (this.checkingAccount.accountNumber && this.checkingAccount.accountNumberConfirm) {
            if (this.checkingAccount.accountNumber === this.checkingAccount.accountNumberConfirm) {
                this.validAccount = true;
                this.accountNumberErrorMsg = null;
                this.accountNumberConfirmErrorMsg = null;
            } else {
                this.validAccount = false;
                this.accountNumberErrorMsg = "Account numbers do not match.";
                this.accountNumberConfirmErrorMsg = "Account numbers do not match.";
            }
        }
    }

    /**
     * Validates account information entered.
     * @returns {boolean} false if routing number, account number, and account nickname are valid, otherwise returns true
     */
    invalid() {
        //we want to return true when it is invalid. and false when it is valid.
        return !(this.isValidRoutingNumber && this.validAccount && this.checkingAccount.nickname);
    }

    preSubmit() {
        if (this.checkingAccount.defaultPaymentMethod && this.paymentSchedules && this.paymentSchedules.length > 0) {
            const dialogRef = this.dialog.open(DetailsPropagateDefaultDialog, {
                data: { paymentSchedules: this.paymentSchedules, type: 'Add' }
            });
            dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
                if (value === 'Save') {
                    this.submit(false);
                } else if (value === 'Update') {
                    this.submit(true);
                }
            });
        } else {
            this.submit(false);
        }
    }

    /**
     *  Starts processing checking account information and submits it to FTNI to retrieve a token before saving to the database.
     */
    submit(propogatePmtMethod: boolean) {
        //verify
        this.spinnerService.showSpinner();

        this.ftniApi.privateProcessACHPost(this.checkingAccount)
            .pipe(take(1)).subscribe({
                next: resp => {
                    // add whatever is needed to payment method in addition to what we get from FTNI
                    // set payment Method object
                    this.tokenResponse = resp;
                    if (this.tokenResponse && this.tokenResponse.BankToken) {
                        this.save(propogatePmtMethod);
                    } else {
                        this.spinnerService.closeSpinner();
                        this.dialog.open(DialogErrorDialog, {
                            data: { error: 'An error occurred when attempting to save your checking account. ' }
                        });
                    }
                }, error: err => {
                    let errMsg: string = undefined;
                    if (err.error && err.error.message) {
                        errMsg = err.error.message;
                    }
                    this.errorHandlerService.handleNetworkError(err, true, errMsg)
                        .pipe(take(1)).subscribe({ next: () => {} });
                } });
    }

    /**
     * Saves new checking account information to the database.
     */
    save(propagatePmtMethod: boolean) {

        let acctNumMasked: string;
        acctNumMasked = this.checkingAccount.accountNumber.length <= 4 ?
            this.checkingAccount.accountNumber.slice(-2) :
            this.checkingAccount.accountNumber.slice(-4);

        this.paymentMethod = {
            routingNumber: this.checkingAccount.routingNumber,
            bankName: this.checkingAccount.bankName,
            defaultMethod: this.checkingAccount.defaultPaymentMethod,
            maskedId: acctNumMasked,
            nickname: this.checkingAccount.nickname,
            token: this.tokenResponse.BankToken + "",
            payerId: this.checkingAccount.payerId,
            bankAccountType: this.checkingAccount.savingsAccountIndicator
        };

        this.paymentMethodApi.paymentMethodsPost(this.paymentMethod)
            .pipe(take(1)).subscribe({
                next: async _resp => {
                    if (propagatePmtMethod && this.paymentSchedules && this.paymentSchedules.length) {
                        this.paymentMethodApi.paymentMethodPropagateToSchedules(this.paymentSchedules, _resp,
                            this.paymentSchedules[0].paymentMethod.type !== 'ach').then(resp => {
                            if (resp && resp.length > 0) {
                                this.dialog.open(DialogErrorDialog, {
                                    data: {
                                        message: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                        'Please verify that the schedules: ' + resp.join(',') + ' were processed ' +
                                        'correctly. If not, you may need to change the payment method manually.'
                                    }
                                });
                            }
                        },
                        () => {
                            this.dialog.open(DialogErrorDialog, {
                                data: { message: 'An error occurred when attempting to save or update Payment Schedules. ' +
                                    'Please verify that the schedules were processed correctly. If not, you may need to ' +
                                    'change the payment method manually.' }
                            });
                        });
                    }
                    this.clearAllData();
                    this.spinnerService.closeSpinner();
                    if (this.comingFromPayNow) {
                        this.router.navigateByUrl('/properties/' + this.propertyId + '/payment',
                            { state: { propertyId: this.propertyId, property: this.property } });
                    } else {
                        this.router.navigateByUrl('/wallet');
                    }
                }, error: err => {

                    if (err && err.status === 400) {

                        this.spinnerService.closeSpinner();
                        this.dialog.open(AccountDeclinedDialog);

                    } else {
                        this.networkErrorService.handleNetworkError(err, false)
                            .pipe(take(1)).subscribe({ next: () => {} });
                    }
                } });
    }

    /**
     * Cancel and return to wallet landing page.
     */
    cancel() {
        this.clearAllData();
        this.router.navigateByUrl('/wallet');
    }

    /**
     * Opens tooltip dialog for routing number.
     */
    routingNumberHelper() {
        this.dialog.open(CheckRoutingNumberHelperDialog);
    }

    /**
     * Open tooltip dialog for checking account number.
     */
    accountNumberHelper() {
        this.dialog.open(CheckAccountNumberHelperDialog);
    }

    /**
     * Clear out all data on the coupon capture.
     */
    clearAllData() {
        this.couponCaptureService.setRoutingNumber("");
        this.couponCaptureService.setAccountNumber("");
    }

}

